import { Box } from "@mui/system"
import { memo, useEffect, useState } from "react"
import ScrollContainer from "react-indiana-drag-scroll"
import { useElementSize } from "usehooks-ts"
import useCanvasWheelZoom from "../../hooks/editor/useCanvasWheelZoom"
import { useCanvas } from "../../hooks/useCanvas"
import useHandTool from "../../hooks/useHandTool"
import { useAppSelector } from "../../hooks/useRedux"
import { CanvasCursor } from "../../store/slices/canvasCursor.slice"
import Canvas from "./Preview/Canvas"
import PreviewTools from "./Preview/PreviewTools"

const Preview = () => {
    const canvasCursorReducer = useAppSelector((state) => state.canvasCursor.value)
    const canvasCursor =
        canvasCursorReducer === CanvasCursor.GrabCursorBySpace
            ? CanvasCursor.GrabCursor
            : canvasCursorReducer
    const enableGrab = canvasCursor !== CanvasCursor.DefaultCursor
    const { canvasResolution: canvas } = useCanvas()
    const canvasZoom = useAppSelector((state) => state.editor.value.settings.canvasZoom)
    const zoomScale: number = canvasZoom / 100

    // zjišťování velikosti plátna, pokud se scaluje např. podle velikosti okna prohlížeče
    // to je nyní nevyužito, ale možná bude potřeba při implementaci lepšího zoomu canvasu
    const [ref, { width, height }] = useElementSize()

    const { ref: canvasRef, handleFocusOnClick } = useHandTool(enableGrab)
    // Cmd/Ctrl + wheel and touchpad pinch, centred on the pointer
    useCanvasWheelZoom(canvasRef)
    const [cursor, setCursor] = useState<CanvasCursor>(canvasCursor)

    useEffect(() => {
        setCursor(canvasCursor)
    }, [canvasCursor])

    // `transform: scale()` does not affect layout, so center-resize-wrapper has to be sized
    // manually. The canvas is scaled from its center, so the wrapper has to hold the whole scaled
    // canvas (zoomScale) plus a one canvas reserve around it - otherwise the outer parts of a
    // zoomed in canvas can never be scrolled to. Below 100 % the unscaled canvas is already
    // bigger than the scaled one, so it is reserve enough.
    const wrapperCoef = zoomScale >= 1 ? zoomScale + 1 : 1

    const calculateWidth = Math.max(canvas.width * wrapperCoef, canvasRef.current?.offsetWidth ?? 0)

    const calculateHeight = canvas.height * wrapperCoef

    return (
        //display: grid ensures correctly displayed previewTool and Preview
        <Box sx={{ display: "grid", height: "100%", minWidth: 600 }}>
            <PreviewTools
                size={{
                    width: width,
                    height: height,
                }}
                canvasRef={canvasRef}
            />
            <ScrollContainer
                innerRef={canvasRef}
                onClick={handleFocusOnClick}
                vertical={enableGrab}
                horizontal={enableGrab}
                hideScrollbars={false}
                onStartScroll={() => enableGrab && setCursor(CanvasCursor.GrabbingCursor)}
                onEndScroll={() => enableGrab && setCursor(canvasCursor)}
                style={{
                    width: "100%",
                    cursor: cursor,
                    outline: "none",
                }}
            >
                {/* this div wrapper is needed because transform: scale in canvas-div-wrapper, 
                for center div when zooming and when canvasWidth is less than canvasRef.width */}
                <div
                    id="center-resize-wrapper"
                    className="center-resize-wrapper"
                    style={{
                        width: calculateWidth,
                        height: calculateHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <div
                        className="canvas-div-wrapper"
                        ref={ref}
                        style={{
                            transform: `scale(${zoomScale})`,
                            width: canvas.width,
                            height: canvas.height,
                            transformOrigin: "center",
                            position: "relative",
                            zIndex: 0,
                        }}
                    >
                        <Canvas
                            size={{
                                width: width,
                                height: height,
                            }}
                            zoomScale={zoomScale}
                        />
                    </div>
                </div>
            </ScrollContainer>
        </Box>
    )
}

export default memo(Preview)
